import React from 'react';
import { Button, Checkbox, ColorPicker, Input, Modal, Select, Slider, Space, Tooltip } from 'antd';
import { message } from '../../utils/antdMessage';
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PushpinOutlined
} from '@ant-design/icons';
import { FONT_OPTIONS } from '../../utils/mapDrawingBoard/constants';
import DrawingToolList from './DrawingToolList';
import DrawingSettingsPanel from './DrawingSettingsPanel';

/**
 * 画板 UI：侧栏 + 右键菜单 + 文字/推送弹层
 * 逻辑由 useMapDrawingBoard 提供，本组件纯展示。
 */
export default function MapDrawingBoardView(p) {
  const {
    expanded, pinned, setPinned,
    handleMouseEnter, handleMouseLeave,
    tool, selectTool,
    drawColor, setDrawColor,
    drawOpacity, setDrawOpacity,
    sketchLineWidth, setSketchLineWidth,
    sketchPreviewColor, setSketchPreviewColor,
    textStyle, setTextStyle,
    textContent, setTextContent,
    textEditOpen, setTextEditOpen,
    statusText, isEditingPath,
    features, setFeatures, featuresRef,
    selectedIds, setSelectedId, setSelectedIds, selectedIdsRef,
    selectedFeature, ctxMenu, setCtxMenu, ctxFeature,
    finishDraft, cancelCurrentDrawing, clearAll,
    startEditFeature, deleteSelectedFeature, patchSelectedFeature,
    allowPush, allowExport,
    openPush, doExport, submitPush,
    pushOpen, setPushOpen, pushFeature, pushForm, setPushForm, pushing,
    memberMeta, filteredGroups, filteredUsers,
    getMap, syncFeaturesToMap,
    formatDistance, formatArea,
    isLineFeatureType, isAreaFeatureType, isEditableFeatureType
  } = p;

  return (
    <>
      <div
        className={`zhxt-draw-board ${expanded || pinned ? 'expanded' : 'collapsed'} ${pinned ? 'pinned' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="zhxt-draw-tab" title="地图画板">
          <span className="zhxt-draw-tab-label">画板</span>
        </div>

        <div className="zhxt-draw-panel">
          <div className="zhxt-draw-header">
            <div className="zhxt-draw-header-left">
              <span className="zhxt-draw-header-status" title="画板可用" />
              <strong>画板</strong>
            </div>
            <Tooltip title={pinned ? '取消固定' : '固定菜单'}>
              <Button
                size="small"
                type={pinned ? 'primary' : 'default'}
                icon={<PushpinOutlined />}
                onClick={() => setPinned((v) => !v)}
              />
            </Tooltip>
          </div>

          <DrawingToolList tool={tool} selectTool={selectTool} />

          <DrawingSettingsPanel
            tool={tool}
            features={features}
            selectedIds={selectedIds}
            selectedFeature={selectedFeature}
            drawColor={drawColor}
            setDrawColor={setDrawColor}
            drawOpacity={drawOpacity}
            setDrawOpacity={setDrawOpacity}
            sketchLineWidth={sketchLineWidth}
            setSketchLineWidth={setSketchLineWidth}
            sketchPreviewColor={sketchPreviewColor}
            setSketchPreviewColor={setSketchPreviewColor}
            textStyle={textStyle}
            setTextStyle={setTextStyle}
            textContent={textContent}
            setTextContent={setTextContent}
            patchSelectedFeature={patchSelectedFeature}
          />

          <div className="zhxt-draw-actions">
            {['route', 'measure', 'polygon'].includes(tool) && (
              <Button size="small" type="primary" onClick={finishDraft}>
                {isEditingPath ? '保存修改' : '完成'}
              </Button>
            )}
            {tool && tool !== 'sketch' && tool !== 'text' && (
              <Button size="small" onClick={cancelCurrentDrawing}>
                {isEditingPath ? '取消编辑' : '取消绘制'}
              </Button>
            )}
            <Button size="small" icon={<ClearOutlined />} danger onClick={clearAll}>清空</Button>
          </div>

          {statusText && <div className="zhxt-draw-status">{statusText}</div>}

          {selectedFeature && (
            <div className="zhxt-draw-selected">
              <div>
                选中：
                {selectedIds.length > 1
                  ? `${selectedIds.length} 项（框选）`
                  : selectedFeature.name}
              </div>
              {selectedIds.length === 1 && selectedFeature.meta?.distance != null && (
                <div>长度：{formatDistance(selectedFeature.meta.distance)}</div>
              )}
              {selectedIds.length === 1 && selectedFeature.meta?.area != null && (
                <div>面积：{formatArea(selectedFeature.meta.area)}</div>
              )}
              {selectedIds.length === 1 && selectedFeature.type === 'text' && (
                <div className="zhxt-draw-text-preview">{selectedFeature.text}</div>
              )}
              {selectedIds.length > 1 && (
                <div className="zhxt-draw-selected-hint" style={{ marginTop: 4 }}>
                  上方颜色/透明度/线宽将批量应用到全部选中项
                </div>
              )}
              <Space wrap style={{ marginTop: 6 }}>
                {selectedIds.length === 1 && isEditableFeatureType(selectedFeature.type) && (
                  <>
                    {(isLineFeatureType(selectedFeature.type) || isAreaFeatureType(selectedFeature.type)) && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => startEditFeature(selectedFeature)}
                      >
                        {isAreaFeatureType(selectedFeature.type) ? '编辑形状' : '编辑路径'}
                      </Button>
                    )}
                    {selectedFeature.type === 'text' && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => startEditFeature(selectedFeature)}
                      >
                        再编辑
                      </Button>
                    )}
                    {selectedFeature.type === 'sketch' && (
                      <Button size="small" onClick={() => startEditFeature(selectedFeature)}>
                        改样式
                      </Button>
                    )}
                    {allowExport && isLineFeatureType(selectedFeature.type) && (
                      <>
                        <Button size="small" onClick={() => doExport(selectedFeature, 'gpx')}>导出GPX</Button>
                        <Button size="small" onClick={() => doExport(selectedFeature, 'kml')}>导出KML</Button>
                      </>
                    )}
                    {allowPush && selectedFeature.type === 'route' && (
                      <Button size="small" type="primary" onClick={() => openPush(selectedFeature)}>推送队员</Button>
                    )}
                    {isAreaFeatureType(selectedFeature.type) && (
                      <Button
                        size="small"
                        onClick={() => {
                          patchSelectedFeature({ fill: !selectedFeature.fill });
                        }}
                      >
                        {selectedFeature.fill ? '取消填充' : '填充'}
                      </Button>
                    )}
                  </>
                )}
                <Button size="small" danger icon={<DeleteOutlined />} onClick={deleteSelectedFeature}>
                  {selectedIds.length > 1 ? `删除选中(${selectedIds.length})` : '删除'}
                </Button>
                {selectedIds.length > 1 && (
                  <Button size="small" onClick={() => setSelectedIds([])}>取消选择</Button>
                )}
              </Space>
              <div className="zhxt-draw-selected-hint">
                {selectedIds.length > 1
                  ? '提示：框选后可批量换色/删除；Shift+再框选可追加'
                  : selectedFeature.type === 'text'
                    ? '提示：可拖动文字位置；侧栏改样式；Del 删除'
                    : selectedFeature.type === 'sketch'
                      ? '提示：点击线条可选中；可改颜色/线宽；Del 删除'
                      : '提示：拖动顶点可改形状；选择工具可框选多个；Del 删除'}
              </div>
            </div>
          )}

          {features.length > 0 && (
            <div className="zhxt-draw-list">
              <div className="zhxt-draw-list-title">图形列表（{features.length}）</div>
              {features.map((f) => (
                <div
                  key={f.id}
                  className={`zhxt-draw-list-item ${selectedIds.includes(f.id) ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedId(f.id);
                    setDrawColor(f.color);
                    setDrawOpacity(f.opacity);
                    if (f.lineWidth) setSketchLineWidth(f.lineWidth);
                  }}
                >
                  <span className="dot" style={{ background: f.color }} />
                  <span className="name">{f.name}</span>
                  {isEditableFeatureType(f.type) && (
                    <button
                      type="button"
                      className="edit"
                      title={
                        f.type === 'text'
                          ? '再编辑文字'
                          : f.type === 'sketch'
                            ? '改样式'
                            : isAreaFeatureType(f.type)
                              ? '编辑形状'
                              : '编辑路径'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditFeature(f);
                      }}
                    >
                      ✎
                    </button>
                  )}
                  <button
                    type="button"
                    className="del"
                    onClick={(e) => {
                      e.stopPropagation();
                      const map = getMap?.();
                      const next = features.filter((x) => x.id !== f.id);
                      setFeatures(next);
                      featuresRef.current = next;
                      setSelectedIds(selectedIdsRef.current.filter((id) => id !== f.id));
                      if (map) syncFeaturesToMap(map, next);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {ctxMenu && ctxFeature && (
        <div
          className="zhxt-draw-context"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onMouseLeave={() => setCtxMenu(null)}
        >
          <div className="title">{ctxFeature.name}</div>
          <button
            type="button"
            onClick={() => {
              startEditFeature(ctxFeature);
              setCtxMenu(null);
            }}
          >
            {ctxFeature.type === 'text'
              ? '再编辑文字'
              : ctxFeature.type === 'sketch'
                ? '改样式'
                : isAreaFeatureType(ctxFeature.type)
                  ? '编辑形状'
                  : '编辑路径'}
          </button>
          {allowExport && isLineFeatureType(ctxFeature.type) && (
            <>
              <button type="button" onClick={() => doExport(ctxFeature, 'gpx')}>导出 GPX</button>
              <button type="button" onClick={() => doExport(ctxFeature, 'kml')}>导出 KML</button>
            </>
          )}
          {allowPush && ctxFeature.type === 'route' && (
            <button type="button" onClick={() => openPush(ctxFeature)}>推送给队员…</button>
          )}
          <button
            type="button"
            onClick={() => {
              setSelectedId(ctxFeature.id);
              setDrawColor(ctxFeature.color);
              setCtxMenu(null);
              message.info('可在侧栏调整颜色与透明度');
            }}
          >
            设置颜色/透明度
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => {
              const map = getMap?.();
              const next = features.filter((f) => f.id !== ctxFeature.id);
              setFeatures(next);
              featuresRef.current = next;
              if (map) syncFeaturesToMap(map, next);
              setSelectedId(null);
              setCtxMenu(null);
            }}
          >
            删除
          </button>
        </div>
      )}

      <Modal
        title="编辑文字"
        open={textEditOpen}
        onCancel={() => setTextEditOpen(false)}
        onOk={() => {
          const val = (textContent || '').trim();
          if (!val) {
            message.warning('请输入文字内容');
            return;
          }
          if (selectedFeature?.type === 'text') {
            patchSelectedFeature({
              text: val,
              name: val.slice(0, 12) || '文字',
              fontFamily: textStyle.fontFamily,
              fontSize: textStyle.fontSize,
              letterSpacing: textStyle.letterSpacing,
              lineHeight: textStyle.lineHeight,
              color: textStyle.color || drawColor,
              bgColor: textStyle.bgColor,
              bgEnabled: textStyle.bgEnabled !== false,
              opacity: textStyle.opacity ?? drawOpacity
            });
            setDrawColor(textStyle.color || drawColor);
            message.success('文字已更新');
          } else {
            setTextContent(val);
            message.info('请在地图上点击放置文字');
          }
          setTextEditOpen(false);
        }}
        okText="保存"
        cancelText="关闭"
        destroyOnClose
        width={420}
      >
        <Input.TextArea
          rows={4}
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
          placeholder="输入临时说明、标注等内容"
          style={{ marginBottom: 12 }}
        />
        <div className="zhxt-draw-row" style={{ marginBottom: 8 }}>
          <span style={{ width: 40 }}>字体</span>
          <Select
            size="small"
            style={{ flex: 1 }}
            value={textStyle.fontFamily}
            options={FONT_OPTIONS}
            onChange={(fontFamily) => setTextStyle((s) => ({ ...s, fontFamily }))}
          />
        </div>
        <div className="zhxt-draw-row" style={{ marginBottom: 8 }}>
          <span style={{ width: 40 }}>字号</span>
          <Slider
            min={10}
            max={48}
            value={textStyle.fontSize}
            onChange={(fontSize) => setTextStyle((s) => ({ ...s, fontSize }))}
            style={{ flex: 1, margin: '0 8px' }}
          />
          <span>{textStyle.fontSize}px</span>
        </div>
        <div className="zhxt-draw-row" style={{ marginBottom: 8 }}>
          <span style={{ width: 40 }}>字距</span>
          <Slider
            min={-2}
            max={12}
            step={0.5}
            value={textStyle.letterSpacing}
            onChange={(letterSpacing) => setTextStyle((s) => ({ ...s, letterSpacing }))}
            style={{ flex: 1, margin: '0 8px' }}
          />
          <span>{textStyle.letterSpacing}</span>
        </div>
        <div className="zhxt-draw-row" style={{ marginBottom: 8 }}>
          <span style={{ width: 40 }}>行距</span>
          <Slider
            min={1}
            max={2.4}
            step={0.1}
            value={textStyle.lineHeight}
            onChange={(lineHeight) => setTextStyle((s) => ({ ...s, lineHeight }))}
            style={{ flex: 1, margin: '0 8px' }}
          />
          <span>{textStyle.lineHeight}</span>
        </div>
        <div className="zhxt-draw-row" style={{ marginBottom: 8 }}>
          <span style={{ width: 40 }}>颜色</span>
          <ColorPicker
            value={textStyle.color}
            size="small"
            onChange={(_, hex) => setTextStyle((s) => ({ ...s, color: hex }))}
          />
          <span style={{ width: 40, marginLeft: 12 }}>背景</span>
          <Checkbox
            checked={textStyle.bgEnabled !== false}
            onChange={(e) => setTextStyle((s) => ({ ...s, bgEnabled: e.target.checked }))}
          />
          <ColorPicker
            value={textStyle.bgColor}
            size="small"
            disabled={textStyle.bgEnabled === false}
            onChange={(_, hex) => setTextStyle((s) => ({ ...s, bgColor: hex }))}
          />
        </div>
      </Modal>

      <Modal
        title="推送路线给队员"
        open={pushOpen}
        onCancel={() => setPushOpen(false)}
        onOk={submitPush}
        confirmLoading={pushing}
        okText="推送"
        cancelText="取消"
        destroyOnClose
        width={480}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6 }}>路线名称</div>
          <Input
            value={pushForm.message}
            onChange={(e) => setPushForm((p) => ({ ...p, message: e.target.value }))}
            placeholder="推送消息/路线说明"
          />
        </div>
        <Checkbox
          checked={pushForm.to_all}
          onChange={(e) => setPushForm((p) => ({
            ...p,
            to_all: e.target.checked,
            org_names: [],
            group_names: [],
            user_names: []
          }))}
        >
          推送给所有人
        </Checkbox>
        <div style={{ marginTop: 12, opacity: pushForm.to_all ? 0.5 : 1, pointerEvents: pushForm.to_all ? 'none' : 'auto' }}>
          <div style={{ marginBottom: 6 }}>组织</div>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%', marginBottom: 10 }}
            placeholder="选择组织"
            value={pushForm.org_names}
            options={memberMeta.organizations.map((o) => ({ label: o, value: o }))}
            onChange={(org_names) => setPushForm((p) => ({
              ...p,
              org_names,
              group_names: [],
              user_names: []
            }))}
          />
          <div style={{ marginBottom: 6 }}>小组</div>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%', marginBottom: 10 }}
            placeholder={pushForm.org_names.length ? '选择小组' : '请先选组织'}
            value={pushForm.group_names}
            options={filteredGroups.map((g) => ({ label: `${g.name}（${g.org}）`, value: g.name }))}
            onChange={(group_names) => setPushForm((p) => ({ ...p, group_names, user_names: [] }))}
          />
          <div style={{ marginBottom: 6 }}>队员</div>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="选择队员"
            value={pushForm.user_names}
            options={filteredUsers.map((u) => ({ label: `${u.name}`, value: u.name }))}
            onChange={(user_names) => setPushForm((p) => ({ ...p, user_names }))}
          />
        </div>
        {pushFeature?.meta?.distance != null && (
          <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
            路线长度：{formatDistance(pushFeature.meta.distance)}，共 {pushFeature.coordinates.length} 个点
          </div>
        )}
      </Modal>
    </>
  );
}
